'use client';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useCallback, useContext, useEffect, useState } from 'react';

import { PropsWithClassname } from '@/app/types';
import { ProjectNews } from '@/app/api';
import { CookiebotContext } from '@/app/components/CookiebotScript/CookiebotScript';

import '@/app/components/ProjectNewsPost/newsPostStyle.css';

interface ProjectNewsPostProps extends PropsWithClassname {
  newsPost: ProjectNews;
  isCollapsed?: boolean;
}

const ProjectNewsPost = ({ newsPost, isCollapsed: initialIsColapsed, className }: ProjectNewsPostProps) => {
  const { t } = useTranslation();
  const { areMarketingCookiesAllowed } = useContext(CookiebotContext);
  const [isCollapsed, setIsCollapsed] = useState(initialIsColapsed ?? false);
  const [isTimeVisible, setIsTimeVisible] = useState(false);

  const handleReadMoreClick = useCallback(() => {
    setIsCollapsed(false);
  }, [setIsCollapsed]);

  // Workaround for hydration errors
  useEffect(() => {
    setIsTimeVisible(!!newsPost.createdAt);
  }, [newsPost.createdAt]);

  return (
    <div
      id={newsPost.id}
      className={classNames(className, 'relative bg-secondary-light rounded-[8px] p-[24px]', {
        'max-h-[200px] overflow-hidden': isCollapsed,
      })}
    >
      <p className="text-neutral-semiTransparent text-[16px] leading-[24px] mb-[20px]">
        {!!newsPost.createdAt && isTimeVisible && format(newsPost.createdAt, t('dateTimeFormat'))}
      </p>
      {newsPost.title && (
        <p>
          <strong>{newsPost.title}</strong>
        </p>
      )}
      {!!newsPost.text && (
        <div
          dangerouslySetInnerHTML={{ __html: newsPost.text }}
          className={classNames('overflow-hidden news-post', {
            'news-post-marketing-cookies-not-accepted': !areMarketingCookiesAllowed,
            'news-post-marketing-cookies-accepted': areMarketingCookiesAllowed,
          })}
        />
      )}
      {isCollapsed && (
        <div className="absolute bottom-0 bg-secondary-light shadow-secondaryGlow w-full">
          <button
            className="block text-primary-mid hover:no-underline mb-[16px] bg-transparent border-none"
            onClick={handleReadMoreClick}
          >
            {t('components.ProjectNewsPost.readMore')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectNewsPost;
